"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import type { GRAVITATIONAL_FORCE_CALCULATOR_STRINGS } from "@/components/tools/gravitational-force-calculator";
import {
  FieldDiagram,
  ForceDiagram,
} from "@/components/tools/physics-fields/gravity-diagram";
import {
  DiagramFrame,
  QuantityField,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { BODIES, findBody } from "@/lib/tool-kit/physics-fields/bodies";
import { STANDARD_GRAVITY } from "@/lib/tool-kit/physics-fields/constants";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import {
  type FieldTarget,
  type ForceTarget,
  type GravityError,
  solveGravitationalField,
  solveGravitationalForce,
} from "@/lib/tool-kit/physics-fields/gravitation";
import {
  fieldWorking,
  forceWorking,
} from "@/lib/tool-kit/physics-fields/gravitation-working";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";
import {
  ACCELERATION_UNITS,
  DISTANCE_UNITS,
  FORCE_UNITS,
  fromBase,
  MASS_UNITS,
  type UnitDef,
} from "@/lib/tool-kit/physics-fields/units";

type Strings = typeof GRAVITATIONAL_FORCE_CALCULATOR_STRINGS;

const MASS_WITH_BODIES: readonly UnitDef[] = [
  ...MASS_UNITS,
  { id: "Me", symbol: "M⊕", factor: 5.9722e24 },
  { id: "Msun", symbol: "M☉", factor: 1.9884e30 },
];

/** 5.9722e24 written for a text box: no plus sign, no float noise. */
function sci(value: number): string {
  return value.toExponential().replace("e+", "e");
}

/** A solved value written so it can be typed back into a box (1.982e20). */
function typed(value: number, sig: number): string {
  return Number(value.toPrecision(sig)).toExponential().replace("e+", "e");
}

function massOf(id: string): number {
  return findBody(id)?.mass ?? 0;
}

function ErrorOrNotice({
  error,
  strings,
}: {
  error: GravityError;
  strings: Strings;
}) {
  const messages: Record<GravityError, string> = {
    missing: strings.errorMissing,
    "non-positive": strings.errorPositive,
    "below-surface": strings.errorBelowSurface,
    "not-finite": strings.errorRange,
  };
  if (error === "missing") {
    return <Notice>{messages.missing}</Notice>;
  }
  return <ErrorNotice>{messages[error]}</ErrorNotice>;
}

/* ── Force between two masses ─────────────────────────────────────────────── */

type ForceKey = "m1" | "m2" | "r" | "F";

const FORCE_FIELDS: Record<
  ForceKey,
  { target: ForceTarget; units: readonly UnitDef[] }
> = {
  m1: { target: "m1", units: MASS_WITH_BODIES },
  m2: { target: "m2", units: MASS_WITH_BODIES },
  r: { target: "distance", units: DISTANCE_UNITS },
  F: { target: "force", units: FORCE_UNITS },
};

interface Scenario {
  m1: [string, string];
  m2: [string, string];
  r: [string, string];
}

const SCENARIOS: Record<string, Scenario> = {
  "earth-moon": {
    m1: [sci(massOf("earth")), "kg"],
    m2: [sci(massOf("moon")), "kg"],
    r: ["384400", "km"],
  },
  "sun-earth": {
    m1: [sci(massOf("sun")), "kg"],
    m2: [sci(massOf("earth")), "kg"],
    r: ["149598000", "km"],
  },
  "person-earth": {
    m1: [sci(massOf("earth")), "kg"],
    m2: ["70", "kg"],
    r: ["6371", "km"],
  },
  "two-people": { m1: ["70", "kg"], m2: ["70", "kg"], r: ["1", "m"] },
  "one-kilogram": { m1: ["1", "kg"], m2: ["1", "kg"], r: ["1", "m"] },
};

export function ForcePanel({
  strings,
  sig,
}: {
  strings: Strings;
  sig: number;
}) {
  const fmt = useToolNumber();
  const [target, setTarget] = useState<ForceTarget>("force");
  const [scenario, setScenario] = useState("earth-moon");
  const start = SCENARIOS["earth-moon"];
  const [text, setText] = useState<Record<ForceKey, string>>({
    m1: start.m1[0],
    m2: start.m2[0],
    r: start.r[0],
    F: "",
  });
  const [unit, setUnit] = useState<Record<ForceKey, string>>({
    m1: start.m1[1],
    m2: start.m2[1],
    r: start.r[1],
    F: "N",
  });

  const pick = (id: string) => {
    setScenario(id);
    const s = SCENARIOS[id];
    if (s) {
      setText((c) => ({ ...c, m1: s.m1[0], m2: s.m2[0], r: s.r[0] }));
      setUnit((c) => ({ ...c, m1: s.m1[1], m2: s.m2[1], r: s.r[1] }));
    }
  };
  const read = (key: ForceKey) =>
    readQuantity(text[key], FORCE_FIELDS[key].units, unit[key]);
  const outcome = solveGravitationalForce(
    { force: read("F"), m1: read("m1"), m2: read("m2"), distance: read("r") },
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const solvedSI: Record<ForceKey, number | null> = {
    m1: value?.m1 ?? null,
    m2: value?.m2 ?? null,
    r: value?.distance ?? null,
    F: value?.force ?? null,
  };
  const changeTarget = (next: ForceTarget) => {
    const previous = (Object.keys(FORCE_FIELDS) as ForceKey[]).find(
      (key) => FORCE_FIELDS[key].target === target
    );
    const si = previous ? solvedSI[previous] : null;
    if (previous && si !== null) {
      const shown = fromBase(FORCE_FIELDS[previous].units, unit[previous], si);
      setText({ ...text, [previous]: typed(shown, sig) });
    }
    setTarget(next);
  };
  const field = (key: ForceKey, label: string, hint?: string) => {
    const spec = FORCE_FIELDS[key];
    const isSolved = spec.target === target;
    const si = solvedSI[key];
    const shown =
      isSolved && si !== null
        ? formatSig(fmt, fromBase(spec.units, unit[key], si), sig)
        : text[key];
    return (
      <QuantityField
        hint={hint}
        id={`gf-${key}`}
        label={label}
        onChange={(x) => {
          setScenario("custom");
          setText({ ...text, [key]: x });
        }}
        onUnit={(x) => setUnit({ ...unit, [key]: x })}
        solved={isSolved}
        unit={unit[key]}
        unitLabel={t(strings.unitFor, { name: label })}
        units={spec.units}
        value={isSolved && si === null ? "" : shown}
      />
    );
  };
  const names = strings.scenarioNames as Record<string, string>;

  return (
    <>
      <SelectField
        id="gf-scenario"
        label={strings.scenarioLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(SCENARIOS).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={scenario}
      />
      <PillGroup
        label={strings.solveFor}
        name="gf-solve"
        onChange={changeTarget}
        options={[
          { value: "force", label: strings.force },
          { value: "m1", label: strings.mass1 },
          { value: "m2", label: strings.mass2 },
          { value: "distance", label: strings.distance },
        ]}
        value={target}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {field("m1", strings.mass1)}
        {field("m2", strings.mass2)}
        {field("r", strings.distance, strings.distanceHint)}
        {field("F", strings.force)}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <ErrorOrNotice
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.forceBetween}
                value={`${formatSig(fmt, value.force, sig)} N`}
              />
              <StatCard
                label={strings.accelerationOf1}
                value={`${formatSig(fmt, value.a1, sig)} m/s²`}
              />
              <StatCard
                label={strings.accelerationOf2}
                value={`${formatSig(fmt, value.a2, sig)} m/s²`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={forceWorking(target, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.forceCaption}>
              <ForceDiagram
                description={strings.forceDiagramDescription}
                id="gf-force-diagram"
                label={strings.forceDiagramLabel}
                m1={value.m1}
                m2={value.m2}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}

/* ── Field strength at altitude ───────────────────────────────────────────── */

type FieldKey = "M" | "R" | "h" | "g";

const FIELD_FIELDS: Record<
  FieldKey,
  { target: FieldTarget | null; units: readonly UnitDef[] }
> = {
  M: { target: "mass", units: MASS_WITH_BODIES },
  R: { target: null, units: DISTANCE_UNITS },
  h: { target: "altitude", units: DISTANCE_UNITS },
  g: { target: "gravity", units: ACCELERATION_UNITS },
};

export function FieldPanel({
  strings,
  sig,
}: {
  strings: Strings;
  sig: number;
}) {
  const fmt = useToolNumber();
  const [target, setTarget] = useState<FieldTarget>("gravity");
  const [body, setBody] = useState("earth");
  const [text, setText] = useState<Record<FieldKey, string>>({
    M: sci(massOf("earth")),
    R: "6371",
    h: "408",
    g: "",
  });
  const [unit, setUnit] = useState<Record<FieldKey, string>>({
    M: "kg",
    R: "km",
    h: "km",
    g: "m/s2",
  });
  const pick = (id: string) => {
    setBody(id);
    const b = findBody(id);
    if (b) {
      setText((c) => ({ ...c, M: sci(b.mass), R: String(b.radius / 1000) }));
      setUnit((c) => ({ ...c, M: "kg", R: "km" }));
    }
  };
  const read = (key: FieldKey) =>
    readQuantity(text[key], FIELD_FIELDS[key].units, unit[key]);
  const outcome = solveGravitationalField(
    {
      mass: read("M"),
      radius: read("R"),
      altitude: read("h"),
      gravity: read("g"),
    },
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const solvedSI: Record<FieldKey, number | null> = {
    M: value?.mass ?? null,
    R: null,
    h: value?.altitude ?? null,
    g: value?.gravity ?? null,
  };
  const changeTarget = (next: FieldTarget) => {
    const previous = (Object.keys(FIELD_FIELDS) as FieldKey[]).find(
      (key) => FIELD_FIELDS[key].target === target
    );
    const si = previous ? solvedSI[previous] : null;
    if (previous && si !== null) {
      const shown = fromBase(FIELD_FIELDS[previous].units, unit[previous], si);
      setText({ ...text, [previous]: typed(shown, sig) });
    }
    setTarget(next);
  };
  const field = (key: FieldKey, label: string, hint?: string) => {
    const spec = FIELD_FIELDS[key];
    const isSolved = spec.target === target;
    const si = solvedSI[key];
    let shown = text[key];
    if (isSolved) {
      shown =
        si === null
          ? ""
          : formatSig(fmt, fromBase(spec.units, unit[key], si), sig);
    }
    return (
      <QuantityField
        hint={hint}
        id={`gf-field-${key}`}
        label={label}
        onChange={(x) => {
          if (key === "M" || key === "R") {
            setBody("custom");
          }
          setText({ ...text, [key]: x });
        }}
        onUnit={(x) => setUnit({ ...unit, [key]: x })}
        solved={isSolved}
        unit={unit[key]}
        unitLabel={t(strings.unitFor, { name: label })}
        units={spec.units}
        value={shown}
      />
    );
  };
  const bodyNames = strings.bodyNames as Record<string, string>;

  return (
    <>
      <SelectField
        id="gf-body"
        label={strings.bodyLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...BODIES.map((b) => ({
            value: b.id,
            label: bodyNames[b.id] ?? b.id,
          })),
        ]}
        value={body}
      />
      <PillGroup
        label={strings.solveFor}
        name="gf-field-solve"
        onChange={changeTarget}
        options={[
          { value: "gravity", label: strings.fieldStrength },
          { value: "mass", label: strings.bodyMass },
          { value: "altitude", label: strings.altitude },
        ]}
        value={target}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {field("M", strings.bodyMass)}
        {field("R", strings.bodyRadius)}
        {field("h", strings.altitude, strings.altitudeHint)}
        {field("g", strings.fieldStrength)}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <ErrorOrNotice
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.fieldStrength}
                value={`${formatSig(fmt, value.gravity, sig)} m/s²`}
              />
              <StatCard
                label={strings.inStandardGravity}
                value={`${formatSig(fmt, value.gravity / STANDARD_GRAVITY, sig)} g₀`}
              />
              <StatCard
                label={strings.shareOfSurface}
                value={`${formatSig(fmt, (value.gravity / value.surfaceGravity) * 100, Math.min(sig, 4))}%`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={fieldWorking(target, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.fieldCaption}>
              <FieldDiagram
                description={strings.fieldDiagramDescription}
                heightRatio={value.altitude / value.radius}
                id="gf-field-diagram"
                label={strings.fieldDiagramLabel}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}
